import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ListChecks, Trash2, Plus, CheckCircle2 } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2);
const normQ = (q) => ({ _k: uid(), prompt: q.prompt || "", options: [...(q.options || []), "", "", "", ""].slice(0, 4), correct_index: q.correct_index || 0 });

function Question({ q, n, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-cream-deep bg-white p-4" data-testid={`qr-question-${q._k}`}>
      <div className="flex items-start gap-3">
        <span className="h-7 w-7 rounded-full bg-brand text-white text-xs font-bold grid place-items-center flex-shrink-0 mt-1">{n}</span>
        <Input data-testid={`qr-prompt-${q._k}`} value={q.prompt} onChange={(e) => onChange({ prompt: e.target.value })} className="font-medium" />
        <Button type="button" size="icon" variant="ghost" data-testid={`qr-remove-${q._k}`} onClick={onRemove} className="h-9 w-9 text-muted-foreground hover:text-rag-red"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mt-3 pl-10">
        {q.options.map((o, oi) => (
          <label key={o} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 cursor-pointer transition-colors ${q.correct_index === oi ? "border-rag-green bg-rag-green-bg" : "border-cream-deep"}`}>
            <input type="radio" data-testid={`qr-correct-${q._k}-${oi}`} checked={q.correct_index === oi} onChange={() => onChange({ correct_index: oi })} className="accent-[#16a34a]" />
            <Input data-testid={`qr-option-${q._k}-${oi}`} value={o} placeholder={`Option ${oi + 1}`} onChange={(e) => onChange({ options: q.options.map((x, k) => (k === oi ? e.target.value : x)) })} className="h-8 border-0 bg-transparent px-1 focus-visible:ring-0" />
            {q.correct_index === oi && <CheckCircle2 className="h-4 w-4 text-rag-green flex-shrink-0" />}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function QuizReview({ course, onSaved }) {
  const [open, setOpen] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [busy, setBusy] = useState(false);

  const openIt = () => { setLessons((course.lessons || []).map((l) => ({ ...l, quiz: (l.quiz || []).map(normQ) }))); setOpen(true); };
  const patch = (li, qi, p) => setLessons((ls) => ls.map((l, i) => (i !== li ? l : { ...l, quiz: l.quiz.map((q, k) => (k === qi ? { ...q, ...p } : q)) })));
  const remove = (li, qi) => setLessons((ls) => ls.map((l, i) => (i !== li ? l : { ...l, quiz: l.quiz.filter((_, k) => k !== qi) })));
  const add = (li) => setLessons((ls) => ls.map((l, i) => (i !== li ? l : { ...l, quiz: [...l.quiz, normQ({})] })));
  const total = lessons.reduce((n, l) => n + l.quiz.length, 0);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: course.title, description: course.description, category: course.category, cpd_hours: Number(course.cpd_hours), thumbnail: course.thumbnail || "", published: true,
        lessons: lessons.map(({ quiz, ...l }) => ({
          ...l,
          quiz: quiz.filter((q) => q.prompt.trim() && q.options.filter((o) => o.trim()).length >= 2)
            .map((q) => { const opts = q.options.filter((o) => o.trim()); return { prompt: q.prompt.trim(), options: opts, correct_index: Math.min(q.correct_index, opts.length - 1) }; }),
        })),
      };
      await api.put(`/courses/${course.id}`, payload);
      toast.success("Quiz questions saved");
      setOpen(false); onSaved?.();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button type="button" size="sm" data-testid={`review-quiz-${course.id}`} onClick={openIt} className="h-8 rounded-full bg-coral hover:bg-coral-hover text-white text-xs"><ListChecks className="h-3.5 w-3.5 mr-1" />Review quizzes</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="quiz-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">Review quiz questions <Badge variant="outline" data-testid="quiz-review-total">{total} questions</Badge></DialogTitle>
            <p className="text-sm text-muted-foreground">{course.title} — edit any wording, tick the correct answer, then save.</p>
          </DialogHeader>
          <div className="space-y-8 py-2">
            {lessons.map((l, li) => (
              <section key={l.id || li} data-testid={`quiz-review-module-${li}`}>
                <div className="flex items-center justify-between mb-3 sticky top-0 bg-white py-2 z-10">
                  <h3 className="font-heading font-bold text-brand">{l.title} <span className="text-muted-foreground font-normal text-sm">· {l.quiz.length} question{l.quiz.length === 1 ? "" : "s"}</span></h3>
                  <Button type="button" size="sm" variant="outline" data-testid={`quiz-review-add-${li}`} onClick={() => add(li)} className="h-7 text-xs rounded-full"><Plus className="h-3 w-3 mr-1" />Question</Button>
                </div>
                <div className="space-y-3">
                  {l.quiz.length === 0 && <p className="text-sm text-muted-foreground">No quiz for this module yet.</p>}
                  {l.quiz.map((q, qi) => <Question key={q._k} q={q} n={qi + 1} onChange={(p) => patch(li, qi, p)} onRemove={() => remove(li, qi)} />)}
                </div>
              </section>
            ))}
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button type="button" data-testid="quiz-review-save" disabled={busy} onClick={save} className="rounded-full bg-coral hover:bg-coral-hover text-white">{busy ? "Saving…" : "Save all questions"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
